import { Component, computed, DestroyRef, ElementRef, inject, output, input, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService, MenuItem, PrimeIcons } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { DialogService } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { fromEvent, merge, scan, startWith, switchMap } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import {
  DEFAULT_UNIT_PRECISION_MAP,
  UnitPrecisionMap,
  UnitPrecisionOverrides
} from '../../helpers/unit-precision.helper';
import {
  UnitsGridCommitEvent,
  UnitsGridCommitMode,
  UnitsGridCommitReason,
  UnitsGridColumn,
  UnitsGridNumericColumn,
  UnitsGridPasteAppliedEvent,
  UnitsGridPasteRange,
  UnitsGridRowFactory,
  UnitsGridPasteTransform
} from './units-grid-input.models';
import {
  exportUnitsGridToExcel,
} from './units-grid-input-interactions.helper';
import {
  buildUnitsGridHeader,
  formatUnitsGridCellDisplayValue,
  getUnitsGridDisplayFractionDigits,
  getUnitsGridFractionDigits,
  getUnitsGridValidationMessage,
  isUnitsGridEditable,
  isUnitsGridNumericColumn,
  resolveUnitsGridPrecision,
} from './units-grid-input-display.helper';
import {
  buildUnitsGridRawRows,
  canDeleteUnitsGridRow,
  insertUnitsGridRowAbove,
  insertUnitsGridRowBelow,
  resetUnitsGridRows
} from './units-grid-input-row-ops.helper';
import {
  isUnitsGridRowCompletelyBlank,
  UnitsGridCommitCoordinator
} from './units-grid-input-commit.helper';
import { UnitsGridFocusCoordinator } from './units-grid-input-focus.helper';
import { UnitsGridClipboardCoordinator } from './units-grid-input-clipboard.helper';
import { UnitsGridViewActionsCoordinator } from './units-grid-input-view-actions.helper';

@Component({
  selector: 'app-units-grid-input',
  templateUrl: './units-grid-input.component.html',
  providers: [ConfirmationService, DialogService],
  standalone: false
})
export class UnitsGridInputComponent {
  // Guardrail: keep this component generic and reusable.
  // Do not add feature-specific business rules here; inject them via inputs/hooks.
  public readonly rows = input.required<FormArray<FormGroup>>();
  public readonly columns = input.required<readonly UnitsGridColumn[]>();
  public readonly userUnits = input<UserUnitsModel | null>(null);
  public readonly tableTitle = input('Grid');
  public readonly resetConfirmationMessage = input('Are you sure that you want to clear this table?');
  public readonly tableClass = input('p-datatable-sm pr-2 pb-2');
  public readonly showValidation = input(true);
  public readonly suppressValidationForCompletelyBlankRows = input(true);
  public readonly showRowNumbers = input(true);
  public readonly allowDeleteRows = input(false);
  public readonly minRows = input(0);
  public readonly enableContextMenu = input(true);
  public readonly enableColumnMultiSelect = input(true);
  public readonly enableClipboardPaste = input(true);
  public readonly enableFileImport = input(true);
  public readonly enableExcelExport = input(true);
  public readonly enableDelimiterDialog = input(true);
  public readonly numberFormatDialogData = input<Readonly<Record<string, unknown>>>({});
  public readonly enableCompareValues = input(true);
  public readonly enableScroll = input(false);
  public readonly scrollHeight = input('420px');
  public readonly minColumnWidthPx = input(66.5);
  public readonly virtualScroll = input(false);
  public readonly virtualScrollThreshold = input(50);
  public readonly virtualScrollItemSize = input(28);
  public readonly commitMode = input<UnitsGridCommitMode>('debounced');
  public readonly commitDebounceMs = input(300);
  public readonly blockCommitWhenValidationErrors = input(true);
  public readonly autoHeight = input(false);
  public readonly autoHeightOffset = input(0);
  public readonly autoHeightDefault = input('600px');
  public readonly autoHeightContainerSelector = input<string | null>('.grid-stack-item-content');
  public readonly autoHeightMin = input(120);
  public readonly createRow = input<UnitsGridRowFactory | null>(null);
  public readonly rowCanBeDeleted = input<((row: FormGroup, rowIndex: number) => boolean) | null>(null);
  public readonly rowActionLabel = input<((row: FormGroup, rowIndex: number) => string | null) | null>(null);
  public readonly rowActionIconSrc = input<((row: FormGroup, rowIndex: number) => string | null) | null>(null);
  public readonly cellTooltip = input<((row: FormGroup, column: UnitsGridColumn, rowIndex: number) => string | null) | null>(null);
  public readonly beforePasteTransform = input<UnitsGridPasteTransform | null>(null);
  public readonly normalizePasteByFirstColumn = input(true);
  public readonly dedupePasteByFirstColumn = input(true);
  public readonly sortPasteByFirstColumn = input(true);
  public readonly unitPrecisionMap = input<UnitPrecisionMap>(DEFAULT_UNIT_PRECISION_MAP);
  public readonly unitPrecisionOverrides = input<UnitPrecisionOverrides>({});

  public readonly cellEdited = output<{ rowIndex: number; field: string; value: unknown }>();
  public readonly rowDeleted = output<number>();
  public readonly rowInserted = output<number>();
  public readonly rowsReset = output<void>();
  // Emitted synchronously just before pasted values are written, so consumers can unlock cells the paste should overwrite.
  public readonly pasteApplying = output<UnitsGridPasteRange>();
  public readonly pasteApplied = output<UnitsGridPasteAppliedEvent>();
  public readonly excelExported = output<{ fileName: string; rowCount: number }>();
  public readonly commitRequested = output<UnitsGridCommitEvent>();

  private autoComputedScrollHeight: string | null = null;
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly commitCoordinator: UnitsGridCommitCoordinator;
  private readonly focusCoordinator: UnitsGridFocusCoordinator;
  private readonly clipboardCoordinator: UnitsGridClipboardCoordinator;
  private readonly viewActionsCoordinator: UnitsGridViewActionsCoordinator;
  private readonly rowsMutationVersion = toSignal(
    toObservable(this.rows).pipe(
      switchMap((rows) => merge(rows.valueChanges, rows.statusChanges).pipe(startWith(null))),
      scan((version) => version + 1, 0)
    ),
    { initialValue: 0 }
  );

  public readonly contextMenuItems: MenuItem[] = [
    { label: 'Insert Row Above', icon: PrimeIcons.ANGLE_UP, command: () => this.insertRowAbove(this.getSelectedRowIndex()) },
    { label: 'Insert Row Below', icon: PrimeIcons.ANGLE_DOWN, command: () => this.insertRowBelow(this.getSelectedRowIndex()) },
    { label: 'Delete Row', icon: PrimeIcons.TRASH, command: () => this.onDeleteRow(null, this.getSelectedRowIndex()) },
    { label: 'Paste', icon: PrimeIcons.COPY, command: () => this.pasteFromClipboard() },
    { label: 'Import CSV/TXT', icon: PrimeIcons.FILE_IMPORT, command: () => this.triggerImportFilePicker() },
    { label: 'Reset Table', icon: PrimeIcons.REPLAY, command: () => this.resetRowsData() }
  ];
  public readonly compareContextMenuItems: MenuItem[];
  @ViewChild('gridContextMenu') gridContextMenu?: ContextMenu;
  @ViewChild('gridCompareMenu') gridCompareMenu?: ContextMenu;
  @ViewChild('unitsGridTable') unitsGridTable?: Table;

  constructor(
    private readonly dialogService: DialogService,
    private readonly confirmationService: ConfirmationService
  ) {
    this.commitCoordinator = new UnitsGridCommitCoordinator({
      getMode: () => this.commitMode(),
      getDebounceMs: () => this.commitDebounceMs(),
      getRows: () => this.rows().controls as FormGroup[],
      getColumns: () => this.columns(),
      shouldBlockWhenInvalid: () => this.blockCommitWhenValidationErrors(),
      emit: (event) => this.commitRequested.emit(event)
    });

    this.focusCoordinator = new UnitsGridFocusCoordinator({
      hostElement: this.hostElement.nativeElement,
      getColumns: () => this.columns(),
      getRows: () => this.tableRows(),
      isNumericColumn: (column) => this.isNumericColumn(column),
      isEditableColumn: (column) => this.isEditable(column),
      getControl: (row, fieldName) => this.getControl(row, fieldName),
      isVirtualScrollEnabled: () => this.useVirtualScroll(),
      scrollToVirtualIndex: (rowIndex) => this.unitsGridTable?.scrollToVirtualIndex(rowIndex)
    });

    this.clipboardCoordinator = new UnitsGridClipboardCoordinator(this.dialogService, {
      enableClipboardPaste: () => this.enableClipboardPaste(),
      enableFileImport: () => this.enableFileImport(),
      enableDelimiterDialog: () => this.enableDelimiterDialog(),
      getColumns: () => this.columns(),
      getBeforePasteTransform: () => this.beforePasteTransform(),
      getNormalizePasteByFirstColumn: () => this.normalizePasteByFirstColumn(),
      getDedupePasteByFirstColumn: () => this.dedupePasteByFirstColumn(),
      getSortPasteByFirstColumn: () => this.sortPasteByFirstColumn(),
      getNumberFormatDialogData: () => this.numberFormatDialogData(),
      getRows: () => this.rows(),
      getCreateRow: () => this.createRow(),
      onPasteApplying: (range) => this.pasteApplying.emit(range),
      onPasteApplied: (event) => this.pasteApplied.emit(event),
      requestCommitAfterPaste: () => this.commitCoordinator.requestCommit('pasteApplied', true)
    });

    this.viewActionsCoordinator = new UnitsGridViewActionsCoordinator(this.dialogService, {
      getColumns: () => this.columns(),
      getRows: () => this.rows().controls as FormGroup[],
      getHeader: (column) => this.getHeader(column),
      enableColumnMultiSelect: () => this.enableColumnMultiSelect(),
      enableCompareValues: () => this.enableCompareValues(),
      enableContextMenu: () => this.enableContextMenu(),
      showGridContextMenu: (event) => this.gridContextMenu?.show(event),
      showCompareContextMenu: (event) => this.gridCompareMenu?.show(event)
    });

    this.compareContextMenuItems = this.viewActionsCoordinator.compareContextMenuItems;

    // Global copy support for selected columns. Kept reactive with automatic teardown.
    fromEvent<ClipboardEvent>(window, 'copy')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (!this.enableColumnMultiSelect()) {
          return;
        }

        const copyText = this.viewActionsCoordinator.buildSelectedColumnsText();
        if (!copyText) {
          return;
        }

        event.clipboardData?.setData('text/plain', copyText);
        event.preventDefault();
      });

    this.destroyRef.onDestroy(() => {
      this.commitCoordinator.destroy();
      this.clipboardCoordinator.destroy();
    });
  }

  // FormArray mutates controls in place, so emit a fresh array reference when row state mutates.
  public readonly tableRows = computed<FormGroup[]>(() => {
    this.rowsMutationVersion();
    return [...(this.rows().controls as FormGroup[])];
  });

  public readonly useVirtualScroll = computed(() => {
    const threshold = this.virtualScrollThreshold();
    return this.virtualScroll() && (threshold <= 0 || this.tableRows().length >= threshold);
  });

  public readonly tableMinWidth = computed(() => {
    const rowNumberWidth = this.enableExcelExport() || this.showRowNumbers() ? 35 : 0;
    const actionColumnWidth = 50;
    return `${this.columns().length * this.minColumnWidthPx() + rowNumberWidth + actionColumnWidth}px`;
  });

  public onInputFocus(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    target?.select();
  }

  public asFormControl(control: AbstractControl | null): FormControl {
    return control as FormControl;
  }

  public getControl(row: AbstractControl, fieldName: string): AbstractControl | null {
    return (row as FormGroup).get(fieldName);
  }

  public isNumericColumn(column: UnitsGridColumn): column is UnitsGridNumericColumn {
    return isUnitsGridNumericColumn(column);
  }

  public isEditable(column: UnitsGridColumn): boolean {
    return isUnitsGridEditable(column);
  }


  public getHeader(column: UnitsGridColumn): string {
    return buildUnitsGridHeader(column, this.userUnits());
  }

  public getMinFractionDigits(column: UnitsGridColumn): number {
    if (!isUnitsGridNumericColumn(column)) {
      return 0;
    }

    return getUnitsGridFractionDigits(
      column,
      this.getResolvedDecimals(column),
      'minFractionDigits'
    );
  }

  public getMaxFractionDigits(column: UnitsGridColumn): number {
    if (!isUnitsGridNumericColumn(column)) {
      return 0;
    }

    return getUnitsGridFractionDigits(
      column,
      this.getResolvedDecimals(column),
      'maxFractionDigits'
    );
  }

  public getDisplayFractionDigits(column: UnitsGridColumn): number {
    if (!isUnitsGridNumericColumn(column)) {
      return 0;
    }

    return getUnitsGridDisplayFractionDigits(column, this.getResolvedDecimals(column));
  }

  public getCellDisplayValue(row: AbstractControl, column: UnitsGridColumn): string {
    const value = (row as FormGroup).get(column.field)?.value;
    return formatUnitsGridCellDisplayValue(value, column, this.getDisplayFractionDigits(column));
  }

  private isBlankControlValue(value: unknown): boolean {
    return value === null
      || value === undefined
      || value === ''
      || (typeof value === 'number' && Number.isNaN(value));
  }

  private normalizeBlurValue(control: AbstractControl): void {
    if (typeof control.value === 'number' && Number.isNaN(control.value)) {
      control.setValue(null, { emitEvent: false });
    }
  }

  public onCellBlur(rowIndex: number, column: UnitsGridColumn, control: AbstractControl | null): void {
    if (!control) {
      return;
    }

    this.normalizeBlurValue(control);

    const row = this.tableRows().at(rowIndex) as FormGroup | undefined;
    if (row && this.isCompletelyBlankRow(row) && this.isBlankControlValue(control.value)) {
      control.markAsPristine();
      control.markAsUntouched();
    } else {
      control.markAsTouched();
    }

    this.cellEdited.emit({
      rowIndex,
      field: column.field,
      value: control.value
    });
    this.commitCoordinator.requestCommit('cellEdited');
  }

  public canDeleteRow(rowIndex: number): boolean {
    const row = this.tableRows().at(rowIndex);
    const canDeleteByGridRules = canDeleteUnitsGridRow({
      allowDeleteRows: this.allowDeleteRows(),
      rowCount: this.tableRows().length,
      minRows: this.minRows(),
      rowIndex
    });

    if (!canDeleteByGridRules || !row) {
      return false;
    }

    return this.rowCanBeDeleted()?.(row, rowIndex) ?? true;
  }

  public getRowActionLabel(row: AbstractControl, rowIndex: number): string | null {
    return this.rowActionLabel()?.(row as FormGroup, rowIndex) ?? null;
  }

  public getRowActionIconSrc(row: AbstractControl, rowIndex: number): string | null {
    return this.rowActionIconSrc()?.(row as FormGroup, rowIndex) ?? null;
  }

  public getCellTooltip(row: AbstractControl, column: UnitsGridColumn, rowIndex: number): string {
    return this.cellTooltip()?.(row as FormGroup, column, rowIndex) ?? '';
  }

  public onDeleteRow(event: Event | null | undefined, rowIndex: number): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    (document.activeElement as HTMLElement)?.blur();

    if (!this.canDeleteRow(rowIndex)) {
      return;
    }

    this.rowDeleted.emit(rowIndex);
  }

  public getValidationMessage(control: AbstractControl | null): string {
    return getUnitsGridValidationMessage(control?.errors);
  }

  public shouldShowValidationError(row: AbstractControl, column: UnitsGridColumn): boolean {
    const control = this.getControl(row, column.field);
    if (!control || !control.invalid || !control.touched) {
      return false;
    }

    if (!this.suppressValidationForCompletelyBlankRows()) {
      return true;
    }

    return !this.isCompletelyBlankRow(row);
  }

  public onCellTab(event: Event, rowIndex: number, columnIndex: number): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();

    this.focusCoordinator.handleTab(rowIndex, columnIndex, keyboardEvent.shiftKey);
  }

  public onCellEnter(event: Event, rowIndex: number, columnIndex: number): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();

    this.focusCoordinator.handleEnter(rowIndex, columnIndex, keyboardEvent.shiftKey);
  }

  public isCompletelyBlankRow(row: AbstractControl, includeDisabled = false): boolean {
    const group = row as FormGroup;
    return isUnitsGridRowCompletelyBlank(group, this.columns(), includeDisabled);
  }

  public onAutoTableHeightChange(height: string): void {
    this.autoComputedScrollHeight = height;
  }

  public getScrollHeight(): string {
    return this.autoComputedScrollHeight ?? this.scrollHeight();
  }

  public onCellPaste(event: ClipboardEvent, rowIndex: number, columnIndex: number): void {
    this.clipboardCoordinator.onCellPaste(event, rowIndex, columnIndex);
  }

  public triggerImportFilePicker(): void {
    this.clipboardCoordinator.triggerImportFilePicker();
  }

  public async onImportFileSelected(event: Event): Promise<void> {
    await this.clipboardCoordinator.onImportFileSelected(event);
  }

  public async applyClipboardText(text: string, startRowIndex: number, startColumnIndex: number): Promise<void> {
    await this.clipboardCoordinator.applyClipboardText(text, startRowIndex, startColumnIndex);
  }

  public onHeaderClick(event: MouseEvent, column: UnitsGridColumn): void {
    this.viewActionsCoordinator.onHeaderClick(event, column);
  }

  public onHeaderContextMenu(event: MouseEvent, column: UnitsGridColumn): void {
    this.viewActionsCoordinator.onHeaderContextMenu(event, column);
  }

  public isHeaderSelected(columnField: string): boolean {
    return this.viewActionsCoordinator.isHeaderSelected(columnField);
  }

  public onCellClick(): void {
    this.viewActionsCoordinator.onCellClick();
  }

  public onCellContextMenu(event: MouseEvent, rowIndex: number, columnIndex: number): void {
    this.viewActionsCoordinator.onCellContextMenu(event, rowIndex, columnIndex);
  }

  public async pasteFromClipboard(): Promise<void> {
    const selectedCell = this.viewActionsCoordinator.getSelectedCell();
    await this.clipboardCoordinator.pasteFromClipboard(selectedCell.rowIndex, selectedCell.columnIndex);
  }

  public insertRowAbove(rowIndex: number): void {
    const insertIndex = insertUnitsGridRowAbove(this.rows(), this.createRow(), rowIndex);
    if (insertIndex !== null) {
      this.rowInserted.emit(insertIndex);
      this.commitCoordinator.requestCommit('rowInserted', true);
    }
  }

  public insertRowBelow(rowIndex: number): void {
    const insertIndex = insertUnitsGridRowBelow(this.rows(), this.createRow(), rowIndex);
    if (insertIndex !== null) {
      this.rowInserted.emit(insertIndex);
      this.commitCoordinator.requestCommit('rowInserted', true);
    }
  }

  public resetRowsData(): void {
    this.confirmationService.confirm({
      message: this.resetConfirmationMessage(),
      accept: () => {
        resetUnitsGridRows(this.rows(), this.createRow(), this.minRows());
        this.rowsReset.emit();
        this.commitCoordinator.requestCommit('rowsReset', true);
      }
    });
  }

  public requestExternalCommit(): void {
    this.commitCoordinator.requestCommit('externalMutation', true);
  }

  // Generic row-scroll support so consumers can jump to a row of interest (e.g. an inserted or highlighted row).
  public scrollToRowIndex(rowIndex: number): void {
    if (rowIndex < 0) {
      return;
    }

    if (this.useVirtualScroll()) {
      this.unitsGridTable?.scrollToVirtualIndex(rowIndex);
      return;
    }

    const cell = this.hostElement.nativeElement.querySelector(`[data-grid-row="${rowIndex}"]`) as HTMLElement | null;
    cell?.closest('tr')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  public async exportToExcel(): Promise<void> {
    if (!this.enableExcelExport()) {
      return;
    }

    const result = await exportUnitsGridToExcel({
      tableTitle: this.tableTitle(),
      columns: this.columns(),
      rows: buildUnitsGridRawRows(this.rows().controls as FormGroup[]),
      getHeader: (column) => this.getHeader(column)
    });

    this.excelExported.emit(result);
  }

  private getSelectedRowIndex(): number {
    return this.viewActionsCoordinator.getSelectedCell().rowIndex;
  }

  private getResolvedDecimals(column: UnitsGridNumericColumn): number {
    return resolveUnitsGridPrecision(
      column,
      this.userUnits(),
      this.unitPrecisionMap(),
      this.unitPrecisionOverrides()
    );
  }
}
